/**
 * ルートプランの条件入力フォーム
 *
 * 作成画面と詳細の条件編集シートで同じものを使う。
 * 日時は「日付 + 時刻」の 2 入力で受け、送信直前に ISO へ組み立てる。
 */

import { HelpCircle } from 'lucide-react'
import { useState } from 'react'

import type { RoutePlanItem, TravelMode } from '@/api/types/route-plan'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import type { PlanCondition } from '../lib'
import { PlanPointField } from './plan-point-field'

const TRAVEL_MODES: { value: TravelMode; label: string }[] = [
  { value: 'driving', label: '車' },
  { value: 'walking', label: '徒歩' },
]

type EndMode = 'origin' | 'place' | 'spot' | 'none'

const END_MODES: { value: EndMode; label: string }[] = [
  { value: 'origin', label: '出発地に戻る' },
  { value: 'place', label: '別の場所' },
  { value: 'spot', label: 'プラン内のスポット' },
  { value: 'none', label: '指定しない' },
]

/** 端点に充てるスポットを選ぶ。件数が読めないのでリスト側をスクロールさせる */
const SpotPicker = ({
  spots,
  selectedPlaceId,
  onPick,
}: {
  spots: RoutePlanItem[]
  selectedPlaceId: number | null
  onPick: (item: RoutePlanItem) => void
}) => (
  <div className="max-h-44 space-y-1.5 overflow-y-auto rounded-lg border p-2">
    {spots.map((item) => (
      <Button
        key={item.place_id}
        type="button"
        variant={item.place_id === selectedPlaceId ? 'default' : 'outline'}
        className="w-full justify-start px-3!"
        onClick={() => onPick(item)}
      >
        <span className="min-w-0 truncate">{item.name}</span>
      </Button>
    ))}
  </div>
)

/** 「時間」「分」の片方だけ入力しても成立させ、合計 0（＝両方空か 0）なら未設定にする */
const toTotalMinutes = (hours: string, minutes: string): number | null => {
  const total = Number(hours || 0) * 60 + Number(minutes || 0)
  if (!Number.isFinite(total) || total <= 0) return null
  return Math.min(total, 1440)
}

/** 0 を表示すると「空にしたのに 0 が残る」形になり未設定へ戻せないので空文字にする */
const toDigits = (value: number): string => (value > 0 ? String(value) : '')

/** 空欄は滞在なし（0 分）として持つ。0 は toDigits で空表示に戻るので入力し直せる */
const toStayMinutes = (value: string): number => {
  const parsed = Number(value || 0)
  if (!Number.isFinite(parsed) || parsed <= 0) return 0
  return Math.min(Math.floor(parsed), 480)
}

type Props = {
  value: PlanCondition
  onChange: (next: PlanCondition) => void
  /** 端点に充てられるプラン内のスポット。作成画面はまだプランがないので渡さない */
  spots?: RoutePlanItem[]
}

export const PlanConditionFields = ({ value, onChange, spots = [] }: Props) => {
  const patch = (partial: Partial<PlanCondition>) => onChange({ ...value, ...partial })

  // 到着地を選んだ直後はまだ座標が空なので、「別の場所」と「指定しない」は値から区別できない
  const [wantsDestination, setWantsDestination] = useState(value.destLat !== null)
  const [wantsDestSpot, setWantsDestSpot] = useState(value.destPlaceId !== null)
  const [wantsOriginSpot, setWantsOriginSpot] = useState(value.originPlaceId !== null)

  const canPickSpot = spots.length > 0
  const originMode: 'place' | 'spot' =
    canPickSpot && (value.originPlaceId !== null || wantsOriginSpot) ? 'spot' : 'place'
  const endMode: EndMode = value.returnToOrigin
    ? 'origin'
    : wantsDestSpot
      ? 'spot'
      : wantsDestination
        ? 'place'
        : 'none'

  const handleOriginMode = (next: 'place' | 'spot') => {
    setWantsOriginSpot(next === 'spot')
    // 座標はスポットを選んだときに上書きされるので、解除時は直前の値を残して手で直せるようにする
    if (next === 'place') patch({ originPlaceId: null })
  }

  const handleEndMode = (next: EndMode) => {
    setWantsDestination(next === 'place')
    setWantsDestSpot(next === 'spot')
    if (next === 'place') {
      patch({ returnToOrigin: false, destPlaceId: null })
      return
    }
    if (next === 'spot') {
      patch({ returnToOrigin: false, destLat: null, destLon: null, destName: '' })
      return
    }
    patch({
      returnToOrigin: next === 'origin',
      destPlaceId: null,
      destLat: null,
      destLon: null,
      destName: '',
    })
  }

  const maxHours = toDigits(Math.floor((value.maxDurationMinutes ?? 0) / 60))
  const maxMinutes = toDigits((value.maxDurationMinutes ?? 0) % 60)

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="plan-title">タイトル</Label>
        <Input
          id="plan-title"
          value={value.title}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder="未入力ならラリー名と日付から付けます"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="plan-date">日付</Label>
          <Input
            id="plan-date"
            type="date"
            value={value.planDate}
            onChange={(e) => patch({ planDate: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="plan-depart">出発時刻</Label>
          <Input
            id="plan-depart"
            type="time"
            value={value.departTime}
            onChange={(e) => patch({ departTime: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>移動手段</Label>
        <div className="flex gap-2">
          {TRAVEL_MODES.map((mode) => (
            <Button
              key={mode.value}
              type="button"
              variant={value.travelMode === mode.value ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => patch({ travelMode: mode.value })}
            >
              {mode.label}
            </Button>
          ))}
        </div>
        {value.travelMode === 'walking' && (
          <p className="text-xs text-muted-foreground">徒歩の最適化はベータ機能のため、所要時間の精度が落ちます</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>出発地</Label>
        {canPickSpot && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant={originMode === 'place' ? 'default' : 'outline'}
              className="flex-1 px-2! text-xs"
              onClick={() => handleOriginMode('place')}
            >
              場所を指定
            </Button>
            <Button
              type="button"
              variant={originMode === 'spot' ? 'default' : 'outline'}
              className="flex-1 px-2! text-xs"
              onClick={() => handleOriginMode('spot')}
            >
              プラン内のスポット
            </Button>
          </div>
        )}
        {originMode === 'spot' ? (
          <>
            <SpotPicker
              spots={spots}
              selectedPlaceId={value.originPlaceId}
              onPick={(item) =>
                patch({
                  originPlaceId: item.place_id,
                  originLat: item.latitude,
                  originLon: item.longitude,
                  originName: item.name,
                })
              }
            />
            <p className="text-xs text-muted-foreground">選んだスポットからスタートし、そこでスタンプを押せます</p>
          </>
        ) : (
          <PlanPointField
            id="plan-origin"
            placeholder="自宅"
            value={{ name: value.originName, latitude: value.originLat, longitude: value.originLon }}
            onChange={(point) =>
              patch({ originName: point.name, originLat: point.latitude, originLon: point.longitude })
            }
          />
        )}
      </div>

      <div className="space-y-1.5">
        <Label>到着地</Label>
        <div className={cn('grid gap-2', canPickSpot ? 'grid-cols-2' : 'grid-cols-3')}>
          {END_MODES.filter((mode) => mode.value !== 'spot' || canPickSpot).map((mode) => (
            <Button
              key={mode.value}
              type="button"
              variant={endMode === mode.value ? 'default' : 'outline'}
              className="px-2! text-xs"
              onClick={() => handleEndMode(mode.value)}
            >
              {mode.label}
            </Button>
          ))}
        </div>
        {endMode === 'none' && <p className="text-xs text-muted-foreground">最後に回ったスポットで終わりになります</p>}
      </div>

      {endMode === 'spot' && (
        <SpotPicker
          spots={spots}
          selectedPlaceId={value.destPlaceId}
          onPick={(item) =>
            patch({
              destPlaceId: item.place_id,
              destLat: item.latitude,
              destLon: item.longitude,
              destName: item.name,
            })
          }
        />
      )}

      {endMode === 'place' && (
        <PlanPointField
          id="plan-dest"
          label="到着地の場所"
          placeholder="ホテル名や駅名"
          value={{ name: value.destName, latitude: value.destLat, longitude: value.destLon }}
          onChange={(point) => patch({ destName: point.name, destLat: point.latitude, destLon: point.longitude })}
        />
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="plan-stay">滞在時間（分）</Label>
          <Input
            id="plan-stay"
            type="number"
            min={0}
            max={480}
            value={toDigits(value.defaultStayMinutes)}
            placeholder="0"
            onChange={(e) => patch({ defaultStayMinutes: toStayMinutes(e.target.value) })}
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-1">
            <Label htmlFor="plan-max-hours">かけられる時間</Label>
            <Popover>
              <PopoverTrigger aria-label="かけられる時間とは" className="text-muted-foreground">
                <HelpCircle size={14} />
              </PopoverTrigger>
              <PopoverContent align="end" className="p-3! text-xs leading-relaxed">
                出発から戻る（または到着）までの合計上限です。移動と滞在を足してこれを超えるスポットは自動で外されます。両方空にすると時間では打ち切らず、全部回る前提で順路を組みます。
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex items-center gap-1.5">
            <Input
              id="plan-max-hours"
              type="number"
              min={0}
              max={24}
              className="min-w-0 flex-1"
              value={maxHours}
              placeholder="—"
              onChange={(e) => patch({ maxDurationMinutes: toTotalMinutes(e.target.value, maxMinutes) })}
            />
            <span className="shrink-0 text-sm text-muted-foreground">時間</span>
            <Input
              id="plan-max-minutes"
              type="number"
              min={0}
              max={59}
              className="min-w-0 flex-1"
              value={maxMinutes}
              placeholder="—"
              onChange={(e) => patch({ maxDurationMinutes: toTotalMinutes(maxHours, e.target.value) })}
            />
            <span className="shrink-0 text-sm text-muted-foreground">分</span>
          </div>
        </div>
      </div>
    </div>
  )
}
